import type { AppRenderField } from '@web/api/types'
import { OutputBlock } from '@web/components/tools/ToolShell'
import { useState } from 'react'

type DataObject = Record<string, unknown>
export const isDataObject = (value: unknown): value is DataObject =>
  !!value && typeof value === 'object' && !Array.isArray(value)

/** Read an authored JSON Pointer without interpreting expressions or inherited properties. */
export function readPointer(value: unknown, pointer: string): unknown {
  if (pointer === '') return value
  if (!pointer.startsWith('/') || /~(?:[^01]|$)/.test(pointer)) return undefined
  let current = value
  for (const encoded of pointer.slice(1).split('/')) {
    const key = encoded.replace(/~1/g, '/').replace(/~0/g, '~')
    if (!current || typeof current !== 'object' || !Object.hasOwn(current, key))
      return undefined
    current = (current as DataObject)[key]
  }
  return current
}

/** Extract transport wrappers while retaining meaningful structured results. */
export function readableResult(result: unknown): unknown {
  if (!isDataObject(result)) return parseStructuredText(result)
  if (result.structured !== undefined) return result.structured
  if (result.structuredContent !== undefined) return result.structuredContent
  if (isDataObject(result.raw) && result.raw.structuredContent !== undefined)
    return result.raw.structuredContent
  if (typeof result.text === 'string' && result.text.trim())
    return parseStructuredText(result.text)
  if (result.raw !== undefined && result.raw !== null) return result.raw
  if (Object.hasOwn(result, 'text')) return result.text
  return result
}

function parseStructuredText(value: unknown): unknown {
  if (typeof value !== 'string' || !/^[\s]*[[{]/.test(value)) return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

export function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (letter) => letter.toUpperCase())
}

function safeLink(value: string): boolean {
  try {
    return ['http:', 'https:'].includes(new URL(value).protocol)
  } catch {
    return false
  }
}

/** Plain data becomes bounded fields, lists, or tables; strings never become HTML. */
export function StructuredValue({
  value,
  depth = 0,
}: {
  value: unknown
  depth?: number
}) {
  if (value === undefined || value === null)
    return <span className="text-faint">—</span>
  if (typeof value === 'boolean') return <span>{value ? 'Yes' : 'No'}</span>
  if (typeof value === 'number') return <span>{String(value)}</span>
  if (typeof value === 'string') {
    if (safeLink(value))
      return (
        <a
          className="break-all text-accent underline"
          href={value}
          target="_blank"
          rel="noopener noreferrer"
        >
          {value}
        </a>
      )
    return (
      <span className="whitespace-pre-wrap break-words">
        {value.length > 4000 ? `${value.slice(0, 4000)}…` : value || '—'}
      </span>
    )
  }
  if (depth >= 4)
    return (
      <span className="text-muted">
        {Array.isArray(value)
          ? `${value.length} items`
          : `${Object.keys(value).length} fields`}
      </span>
    )
  if (Array.isArray(value)) {
    if (!value.length) return <span className="text-muted">No items</span>
    const shown = value.slice(0, 40)
    const columns = [
      ...new Set(
        shown.flatMap((item) => (isDataObject(item) ? Object.keys(item) : [])),
      ),
    ]
    const table =
      columns.length > 0 &&
      columns.length <= 8 &&
      shown.every(
        (item) =>
          isDataObject(item) &&
          Object.values(item).every(
            (cell) => cell === null || typeof cell !== 'object',
          ),
      )
    return (
      <>
        {table ? (
          <div className="tool-data-table overflow-x-auto">
            <table className="w-full border-collapse text-left text-[12px]">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column}
                      className="border-b border-line px-2 py-1.5 font-medium text-muted"
                    >
                      {humanizeKey(column)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((item, index) => (
                  <tr key={index}>
                    {columns.map((column) => (
                      <td
                        key={column}
                        className="border-b border-line px-2 py-1.5 align-top"
                      >
                        <StructuredValue
                          value={(item as DataObject)[column]}
                          depth={depth + 1}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <ul className="space-y-2 pl-4 list-disc">
            {shown.map((item, index) => (
              <li key={index}>
                <StructuredValue value={item} depth={depth + 1} />
              </li>
            ))}
          </ul>
        )}
        {value.length > shown.length && (
          <p className="mt-1 text-[11px] text-faint">
            {value.length - shown.length} more items in technical details
          </p>
        )}
      </>
    )
  }
  const entries = Object.entries(value).slice(0, 20)
  if (!entries.length)
    return <span className="text-muted">No additional details</span>
  return (
    <dl className="tool-data-fields space-y-2">
      {entries.map(([key, item]) => (
        <div key={key}>
          <dt className="mb-0.5 text-[11px] font-medium text-muted">
            {humanizeKey(key)}
          </dt>
          <dd className="min-w-0 text-[12.5px]">
            <StructuredValue value={item} depth={depth + 1} />
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function AuthoredFields({
  value,
  fields,
}: {
  value: unknown
  fields?: AppRenderField[]
}) {
  const entries = (fields ?? [])
    .map((field) => ({ ...field, value: readPointer(value, field.pointer) }))
    .filter((field) => field.value !== undefined)
  if (!entries.length) return null
  return (
    <dl className="tool-data-fields space-y-2">
      {entries.map((field, index) => (
        <div key={`${field.pointer}:${index}`}>
          <dt className="mb-0.5 text-[11px] font-medium text-muted">
            {field.label}
          </dt>
          <dd className="min-w-0 text-[12.5px]">
            <StructuredValue value={field.value} />
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function TechnicalDetails({
  args,
  result,
  toolName,
}: {
  args: unknown
  result: unknown
  toolName?: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <details
      className="mt-3 text-[11px] text-muted"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="cursor-pointer">Technical details</summary>
      {open && (
        <>
          {toolName && (
            <>
              <div className="label mb-1 mt-2">Tool</div>
              <code className="mono block break-all rounded-control border border-line bg-inset px-3 py-2 text-[12px]">
                {toolName}
              </code>
            </>
          )}
          <div className="label mb-1 mt-2">Input</div>
          <OutputBlock
            text={JSON.stringify(args, null, 2) ?? ''}
            maxLines={20}
          />
          {result !== undefined && (
            <>
              <div className="label mb-1 mt-2">Output</div>
              <OutputBlock
                text={JSON.stringify(result, null, 2) ?? ''}
                maxLines={20}
              />
            </>
          )}
        </>
      )}
    </details>
  )
}
